import React from "react";
import { TextInput } from "flowbite-react";
import { ArrowRight, Search, Sparkles, ShieldCheck, Star, MapPin } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import AppHeader from "./AppHeader";
import TherapistCardSkeleton from "./TherapistCardSkeleton";
import { Therapist } from "../types/therapist";
import { subscribePublicTherapists } from "../services/publicTherapistsService";
import {
  CONSTELLATION_PLATFORM_PRICE,
  CONSTELLATION_SOCIAL_PRICE,
  ConstellationOffer,
  appearsInPlatformVitrine,
  appearsInSocialVitrine,
  formatConstellationPrice,
  priceFromTherapistConstellation,
} from "../constants/planCommerce";
import ReviewStars from "./ReviewStars";
import { formatReviewCountLabel } from "../utils/reviewLabel";

const getInitials = (name: string) => {
  if (!name) return "TT";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();
};

const Therapists: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [therapists, setTherapists] = React.useState<Therapist[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState("");

  React.useEffect(() => {
    setError(null);
    setIsLoading(true);
    setIsRefreshing(false);

    // Tempo real: marcar/desmarcar no painel atualiza a vitrine na hora.
    const unsubscribe = subscribePublicTherapists(
      (list) => {
        setTherapists(list);
        setIsLoading(false);
        setIsRefreshing(false);
        setError(list.length === 0 ? "Nenhum constelador disponível no momento." : null);
      },
      (err) => {
        console.error("Erro ao buscar terapeutas:", err);
        setIsLoading(false);
        setIsRefreshing(false);
        setError(err?.message || "Erro ao carregar terapeutas.");
      },
    );

    return () => {
      unsubscribe();
    };
  }, []);

  const socialVitrine = searchParams.get("oferta") === ConstellationOffer.SOCIAL;

  const filteredTherapists = React.useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    const list = q
      ? therapists.filter(
          (t) =>
            (t.nomePublico || "").toLowerCase().includes(q) ||
            (t.title || "").toLowerCase().includes(q) ||
            (t.especialidade || "").toLowerCase().includes(q),
        )
      : therapists;
    const byOffer = list.filter((t) =>
      socialVitrine ? appearsInSocialVitrine(t) : appearsInPlatformVitrine(t),
    );
    return [...byOffer].sort((a, b) =>
      (a.nomePublico || "").localeCompare(b.nomePublico || "", "pt-BR", {
        sensitivity: "base",
      }),
    );
  }, [therapists, searchQuery, socialVitrine]);

  const showSkeletons = isLoading && therapists.length === 0;

  return (
    <>
      <AppHeader />

      <main className="min-h-screen bg-[#F3F6F1] pt-24 pb-12 sm:pt-28 sm:pb-16">
        <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 sm:px-6">
          {/* Hero */}
          <section className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#2E4B4C] via-[#27413F] to-[#1F3637] shadow-[0_30px_90px_rgba(46,75,76,0.30)] ring-1 ring-white/10">
            {/* Glows decorativos */}
            <div className="pointer-events-none absolute -right-20 -top-24 h-80 w-80 rounded-full bg-[#A56096]/25 blur-3xl" aria-hidden="true" />
            <div className="pointer-events-none absolute -bottom-24 left-1/4 h-72 w-72 rounded-full bg-[#7FB8A3]/20 blur-3xl" aria-hidden="true" />
            {/* Textura pontilhada */}
            <div
              className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:radial-gradient(circle_at_1px_1px,_#ffffff_1px,_transparent_0)] [background-size:22px_22px]"
              aria-hidden="true"
            />

            <div className="relative grid min-h-[320px] items-stretch lg:min-h-[380px] lg:grid-cols-[1.15fr_0.85fr]">
              <div className="flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-12">
                <span className="inline-flex w-fit items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-[#CDE4D9] backdrop-blur">
                  <Sparkles className="h-3.5 w-3.5 text-[#E7B7D6]" aria-hidden="true" />
                  Consteladores certificados
                </span>

                <h1 className="mt-5 max-w-2xl text-3xl font-bold leading-[1.08] tracking-tight text-[#F8F5ED] sm:text-4xl lg:text-5xl">
                  {socialVitrine ? (
                    <>
                      Projeto social com{" "}
                      <span className="bg-gradient-to-r from-[#E7B7D6] to-[#C98BB6] bg-clip-text text-transparent">
                        segurança
                      </span>
                    </>
                  ) : (
                    <>
                      Escolha Seu Constelador com{" "}
                      <span className="bg-gradient-to-r from-[#E7B7D6] to-[#C98BB6] bg-clip-text text-transparent">
                        Segurança
                      </span>
                    </>
                  )}
                </h1>

                <p className="mt-5 max-w-xl text-sm leading-7 text-[#D9E5E0] sm:text-base">
                  {socialVitrine
                    ? `Consteladores do projeto social, sessão a ${formatConstellationPrice(CONSTELLATION_SOCIAL_PRICE)}. Homologados pela plataforma.`
                    : `Consteladores da plataforma, sessão a ${formatConstellationPrice(CONSTELLATION_PLATFORM_PRICE)}. Homologados, avaliados na prática e com pagamento seguro.`}
                </p>

                {/* Pills de confiança */}
                <div className="mt-6 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-[#EAF3EE] ring-1 ring-white/10 backdrop-blur">
                    <ShieldCheck className="h-4 w-4 text-[#9FD8C2]" aria-hidden="true" /> Homologados
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-[#EAF3EE] ring-1 ring-white/10 backdrop-blur">
                    <Star className="h-4 w-4 text-[#9FD8C2]" aria-hidden="true" /> Avaliados na prática
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-[#EAF3EE] ring-1 ring-white/10 backdrop-blur">
                    <MapPin className="h-4 w-4 text-[#9FD8C2]" aria-hidden="true" /> Para todo o Brasil
                  </span>
                </div>
              </div>

              <div className="relative hidden min-h-[220px] lg:block">
                <img
                  src="/assets/terapeutas.gif"
                  alt="Constelador conduzindo uma sessão"
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#2E4B4C]/10 to-[#2E4B4C]/60" />
              </div>
            </div>
          </section>

          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${
                !socialVitrine ? "bg-[#4A7C75] text-white" : "bg-white text-[#4A7C75] ring-1 ring-[#4A7C75]/30"
              }`}
            >
              Constelações · {formatConstellationPrice(CONSTELLATION_PLATFORM_PRICE)}
            </button>
            <button
              type="button"
              onClick={() => setSearchParams({ oferta: ConstellationOffer.SOCIAL })}
              className={`rounded-full px-4 py-2 text-sm font-semibold ${
                socialVitrine ? "bg-[#A56096] text-white" : "bg-white text-[#A56096] ring-1 ring-[#A56096]/30"
              }`}
            >
              Projeto social · {formatConstellationPrice(CONSTELLATION_SOCIAL_PRICE)}
            </button>
          </div>

          {/* Busca */}
          <section className="rounded-[1.5rem] border border-[#D5E1DB] bg-white/95 p-4 shadow-[0_14px_35px_rgba(58,88,89,0.08)] sm:p-5">
            <div className="mx-auto flex max-w-3xl flex-col gap-3">
              <TextInput
                id="search"
                className="border-[#A56096]"
                placeholder="Buscar por nome, título ou especialidade..."
                icon={Search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Buscar terapeutas"
              />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-[#496566]">
                {showSkeletons ? (
                  <span>Carregando perfis...</span>
                ) : (
                  <span>
                    {filteredTherapists.length} {filteredTherapists.length === 1 ? "perfil disponível" : "perfis disponíveis"} nesta vitrine
                  </span>
                )}
                {isRefreshing && (
                  <span className="text-xs text-[#85A7A8] animate-pulse">atualizando lista...</span>
                )}
              </div>
            </div>
          </section>

          {error && (
            <p className="rounded-2xl border border-red-200 bg-red-50 px-4 py-6 text-center text-red-700" role="alert">
              {error}
            </p>
          )}

          {/* Grid */}
          {showSkeletons ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <TherapistCardSkeleton key={i} />
              ))}
            </div>
          ) : !error && therapists.length > 0 ? (
            filteredTherapists.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {filteredTherapists.map((t) => (
                  <article
                    key={t.id}
                    className="group flex h-full flex-col rounded-[1.75rem] border border-[#E8EFEB] bg-white p-6 shadow-[0_18px_40px_rgba(58,88,89,0.1)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_50px_rgba(58,88,89,0.14)]"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="relative">
                        {t.profilePhotoURL ? (
                          <img
                            src={t.profilePhotoURL}
                            alt={t.nomePublico}
                            loading="lazy"
                            decoding="async"
                            className="h-20 w-20 rounded-full object-cover ring-4 ring-[#E8EFEB] transition-transform group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#4A7C75] text-lg font-bold text-white ring-4 ring-[#E8EFEB]">
                            {getInitials(t.nomePublico)}
                          </div>
                        )}
                      </div>
                      <h2 className="mt-4 text-xl font-bold tracking-tight text-[#6C4E78]">{t.nomePublico}</h2>
                      {t.title && (
                        <p className="mt-1 text-sm font-semibold text-[#4A7C75]">{t.title}</p>
                      )}
                      <div className="mt-3 flex flex-col items-center gap-1">
                        {t.reviewCount ? (
                          <>
                            <ReviewStars value={t.reviewAverage || 0} size="sm" />
                            <p className="text-xs text-[#85A7A8]">
                              {(t.reviewAverage || 0).toFixed(1)} · {formatReviewCountLabel(t.reviewCount)}
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-[#85A7A8]">Ainda sem avaliações</p>
                        )}
                        {socialVitrine && appearsInSocialVitrine(t) && (
                          <p className="mt-2 rounded-full bg-[#FDF8FC] px-3 py-0.5 text-xs font-semibold text-[#A56096]">
                            Projeto social
                          </p>
                        )}
                        {formatConstellationPrice(priceFromTherapistConstellation(t, socialVitrine ? ConstellationOffer.SOCIAL : ConstellationOffer.PLATFORM) || (socialVitrine ? CONSTELLATION_SOCIAL_PRICE : CONSTELLATION_PLATFORM_PRICE)) && (
                          <p className="mt-1 text-sm font-bold text-[#2E4B4C]">
                            {formatConstellationPrice(priceFromTherapistConstellation(t, socialVitrine ? ConstellationOffer.SOCIAL : ConstellationOffer.PLATFORM) || (socialVitrine ? CONSTELLATION_SOCIAL_PRICE : CONSTELLATION_PLATFORM_PRICE))}
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="mt-4 flex-grow text-center text-sm leading-6 text-[#5A7272] line-clamp-4 min-h-[5.5rem]">
                      {t.bio}
                    </p>
                    <Link
                      to={socialVitrine ? `/terapeuta/${t.id}?oferta=social` : `/terapeuta/${t.id}`}
                      className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#4A7C75] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#406b65] focus:outline-none focus:ring-2 focus:ring-[#4A7C75] focus:ring-offset-2"
                    >
                      Ver mais
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <div className="rounded-[1.75rem] border border-dashed border-[#D5E1DB] bg-white/80 px-6 py-12 text-center">
                <p className="text-sm text-[#5A7272]">
                  {searchQuery
                    ? `Nenhum terapeuta encontrado para “${searchQuery}”`
                    : socialVitrine
                      ? "Nenhum constelador no projeto social no momento."
                      : "Nenhum constelador nesta vitrine no momento."}
                </p>
              </div>
            )
          ) : null}
        </div>
      </main>
    </>
  );
};

export { Therapists };
